import moment from "moment";
import { useState } from "react";
import { BiCheck, BiCheckDouble, BiPlus } from "react-icons/bi";
import { BsMicMute } from "react-icons/bs";
import { FaShare } from "react-icons/fa6";
import Api from "../../../service/http";
import { useSocket } from "../../../service/SocketProvider";
import AudioMessage from "./AudioMessage";
import ImageMessage from "./ImageMessage";
import ImagesMessage from "./ImagesMessage";
import MessageAction from "./MessageAction";
import PdfMessage from "./PdfMessage";
import ReplyPreview from "./ReplyPreview";
import TextMessage from "./TextMessage";
import VideoMessage from "./VideoMessage";

const Message = ({ message, isSender, setReply, theme }) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const { socketSend } = useSocket();
  const [isPlusDisplayed, setIsPlusDisplayed] = useState(false);

  const selectEmoji = async (emoji) => {
    const res = await Api.post(`/web/chat/${message?.id}/action`, {
      type: 'react',
      emoji,
    });
    socketSend({
      type: "WebFetchMessage",
      user_id: user?.id,
      message: res?.data,
      isScroll: false,
    });
    setIsPlusDisplayed(false);
  };

  const emojiOptions = ["😍", "😂", "😢", "😡", "👍"];

  const renderMessage = () => {
    if (message?.delete_from_everyone) {
      return (
        <div className="text-sm italic opacity-70">
          {(isSender ? "You" : message?.sender?.first_name) + " unsent this message"}
        </div>
      );
    }

    if (message?.is_block) {
      return (
        <p className="text-sm text-red-500 italic">
          This user has been blocked by you.
        </p>
      );
    }

    switch (message?.message_type) {
      case "TXT": return <TextMessage message={message} />;
      case "IMG": return <ImageMessage message={message} />;
      case "IMGS": return <ImagesMessage message={message} />;
      case "VID": return <VideoMessage message={message} />;
      case "AUD": return <AudioMessage message={message} />;
      case "PDF": return <PdfMessage message={message} />;
      default:
        return (
          <a href={message?.media?.file} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
            Download Document
          </a>
        );
    }
  };

  if (message?.message_type === "ACT") {
    return (
      <div className="flex justify-center my-2">
        <p className="text-xs px-3 py-1 rounded-full bg-gray-100 text-gray-500 italic">
          {isSender ? "You" : message?.sender?.first_name} {message?.message}
        </p>
      </div>
    );
  }

  if (message?.deleted_list?.includes(user?.id)) return null;

  return (
    <div
      id={`message-${message?.id}`}
      className={`flex items-start gap-3 mb-6 ${isSender ? "flex-row-reverse" : "flex-row"}`}
    >
      {/* Avatar */}
      <div className="relative shrink-0 mt-1">
        <img
          src={message?.sender?.dp ?? "https://placehold.co/400"}
          className="w-8 h-8 rounded-full border border-gray-100 shadow-sm object-cover"
          alt="avatar"
        />
        {/* <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white bg-green-500"></div> */}
      </div>

      {/* Message Content Container */}
      <div className={`flex flex-col ${isSender ? "items-end text-right" : "items-start text-left"} max-w-[85%] sm:max-w-[75%]`}>
        {!isSender && (
          <p className="font-medium text-[11px] mb-1 px-1 text-gray-500">
            {message?.sender?.first_name || "Unknown"}
            {message?.is_mute && <BsMicMute className="inline ml-1 opacity-60" />}
          </p>
        )}

        <div className={`flex items-end gap-2 group ${isSender ? "flex-row-reverse" : "flex-row"}`}>
          {/* Bubble */}
          <div className={`relative px-4 py-2.5 rounded-2xl text-sm shadow-sm transition-all duration-200 ${
            isSender
              ? "bg-indigo-600 text-white rounded-tr-none"
              : "bg-white text-gray-800 border border-gray-100 rounded-tl-none hover:shadow-md"
          }`}>
            {message?.forwarded ? (
              <p className="text-[10px] mb-1 flex items-center gap-1 opacity-70 italic">
                <FaShare size={10} /> Forwarded
              </p>
            ) : null}

            {message?.reply_to ? (
              <div className={`mb-2 p-2 rounded-lg border-l-4 text-xs opacity-90 ${
                isSender ? 'bg-indigo-500/50 border-white/40' : 'bg-gray-50 border-indigo-400'
              }`}>
                <ReplyPreview message={message.reply_to} />
              </div>
            ) : null}

            <div className="break-words leading-relaxed">
              {renderMessage()}
            </div>

            {/* Reactions */}
            {message?.reactions?.length > 0 && (
              <div className={`absolute -bottom-2.5 ${isSender ? 'left-2' : 'right-2'} flex gap-0.5 bg-white shadow-md rounded-full px-1.5 py-0.5 border border-gray-100 z-10`}>
                {message.reactions.map((reaction, idx) => (
                  <span key={idx} className="text-[11px]" title={reaction?.user?.first_name}>{reaction.emoji}</span>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className={`opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 ${isSender ? 'flex-row-reverse' : 'flex-row'}`}>
            {!message?.delete_from_everyone && (
              <MessageAction message={message} isSender={isSender} setReply={setReply} />
            )}
            <div className="relative">
              <button 
                onClick={() => setIsPlusDisplayed(!isPlusDisplayed)}
                className="p-1 rounded-full hover:bg-gray-100 text-gray-400 transition-colors"
              >
                <BiPlus size={18} />
              </button>
              {isPlusDisplayed && (
                <div className={`absolute bottom-full mb-2 ${isSender ? 'right-0' : 'left-0'} flex gap-1 bg-white shadow-xl border border-gray-100 rounded-full px-2 py-1 z-20`}>
                  {emojiOptions.map(emoji => (
                    <button key={emoji} onClick={() => selectEmoji(emoji)} className="hover:scale-125 transition-transform px-0.5">
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Timestamp */}
        <div className={`flex items-center gap-1.5 mt-1 px-1 ${isSender ? "flex-row-reverse" : "flex-row"}`}>
          <span className="text-[10px] font-medium uppercase tracking-tighter text-gray-400" title={moment(message?.created_at).format("DD MMM YYYY, hh:mm:ss A")}>
            {moment(message?.created_at).format("hh:mm A")}
          </span>
          {isSender && (
            <div className="flex items-center">
              {message?.seen_by_all ? (
                <BiCheckDouble className="text-indigo-400" size={16} title="Seen by all" />
              ) : message?.delivered_to_all ? (
                <BiCheckDouble className="text-gray-300" size={16} title="Delivered to all" />
              ) : (
                <BiCheck className="text-gray-300" size={16} title="Sent" />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Message;
